import React, { useContext, useEffect, useState } from 'react'
import { Box, Button, Paper, List, ListItem, Snackbar, Alert } from '@mui/material'
import { Grading, PersonOutlined, SettingsOutlined, Logout, LockOpen, DescriptionOutlined, HelpOutlineOutlined, Webhook, Build, CloudUpload, BugReport } from '@mui/icons-material'
import logo from '../../Images/mesh-logo-white.svg'
import { useLocation, useNavigate } from 'react-router-dom';
import { signOut } from '@aws-amplify/auth';
import WhoAmIContext from '../../services/whoami';

const navItems = [
  // Removing incomplete nav items
  // {
  //   name: "Dashboard",
  //   path: "/dashboard",
  // },
  // {
  //   name: "Credentials",
  //   path: "/credentials",
  // },
  // {
  //   name: "Professionals",
  //   path: "/professionals",
  // },
  // {
  //   name: "Batch Orders",
  //   path: "/batchOrders",
  //   icon: <Grading />,
  // },
  // {
  //   name: "Profiles",
  //   path: "/profiles",
  //   icon: <PersonOutlined />,
  // },
  {
    name: "Orders",
    path: "/orders",
    icon: <Grading />,
  },
  {
    name: "Bulk Upload",
    path: "/bulkUpload",
    icon: <CloudUpload />,
    requirement: 'bulkUploadEnabled',
  },
  {
    name: "Settings",
    path: '/settings',
    icon: <SettingsOutlined />,
    subPaths: [
      // {
      //   name: 'Profile',
      //   path: '/settings/profile',
      // },
      {
        name: 'Users',
        path: '/settings/users',
        icon: <PersonOutlined />,
      },
      {
        name: 'API Keys',
        path: '/settings/apiKeys',
        icon: <LockOpen />,
      },
      {
        name: 'Webhooks',
        path: '/settings/webhooks',
        icon: <Webhook />,
        requirement: 'webhookManagementEnabled',
      },
      // {
      //   name: 'Billing',
      //   path: '/settings/billing',
      // },
    ],
  },
  {
    name: 'Documentation',
    path: '/customerDocs/guides/mesh/latest/plv-api-integration-guide.md',
    icon: <DescriptionOutlined />,
  },
  {
    name: 'API Playground',
    path: '/apiPlayground',
    icon: <Build />,
    openInNewTab: false,
  },
  {
    name: 'About API',
    path: '/apiDocs',
    icon: <HelpOutlineOutlined />,
    openInNewTab: true,
  },
  {
    name: 'BEV Query Tool',
    path: '/devQueryTool',
    icon: <BugReport />,
    requirement: 'bevQueryToolEnabled',
  },
]

const Drawer = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { webhookManagementEnabled, bulkUploadEnabled, bevQueryToolEnabled, refreshWhoAmI } = useContext(WhoAmIContext)
  const [signOutError, setSignOutError] = useState(null)

  useEffect(() => {
    refreshWhoAmI?.()
  }, [location.pathname, refreshWhoAmI])

  const handleSignOut = async () => {
    try {
      await signOut()
      window.location.href = '/login'
    } catch (err) {
      setSignOutError(
        err?.message ?? 'There was an error logging out. Please clear your cookies and try again.',
      )
    }
  }

  const availableData = {
    webhookManagementEnabled,
    bulkUploadEnabled,
    bevQueryToolEnabled,
  }


  const drawerRequirements = (navItem) => {
    if (navItem.requirement) {
      if(!availableData[navItem.requirement]) {
        return false
      }
    }
    return true
  }

  const goToPage = (path, openInNewTab) => {
    if (openInNewTab) {
      openLinkInNewTab(path)
      return
    }
    navigate(path, { replace: false })
  }

  const openLinkInNewTab = (path) => {
    const fullPath = `${window.location.origin}${path}`
    window.open(fullPath, '_blank')
  }

  return (
    <>
      <Box height={'100%'}>
        <Paper elevation={2} square sx={{
          boxSizing: 'border-box',
          display: 'flex',
          width: '15.5rem',
          flexDirection: 'column',
          alignItems: 'space-between',
          height: '100%',
          backgroundColor: 'emphasis.dark',
        }}>
          <Box container height="100%" sx={{
            padding: '1.5rem 0rem 1.5rem 0rem',
          }}>
            <Box sx={{ padding: '0rem 1.5rem 0rem 1.5rem' }}>
              <LogoBox />
            </Box>
            <List sx={{ padding: '0rem 1.5rem 0rem 1.5rem' }}>
            {navItems
            .filter(drawerRequirements)
            .map((val, index) => (
              <NavItem
                filter={drawerRequirements}
                key={index}
                text={val.name}
                icon={val.icon}
                onPressed={({ path }) => goToPage(path || val.path, val.openInNewTab)}
                currentLocation={location.pathname}
                isActive={location.pathname.includes(val.path)}
                disabled={val.name === 'Batch Orders' || 'Settings' ? false : true}
                item={val}
              />
            ))}
            </List>
            
          </Box>
          <LogOut signOut={handleSignOut} />
        </Paper>
      </Box>
      <Snackbar
        open={Boolean(signOutError)}
        autoHideDuration={6000}
        onClose={() => setSignOutError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSignOutError(null)} severity="error" variant="filled">
          {signOutError}
        </Alert>
      </Snackbar>
    </>
  )
}

const LogoBox = () => {
  return (
    <Box width='100%'>
      <a href="/">
        <img src={logo} style={{
          width: '7.5rem',
          margin: '0',
          paddingBottom: '3rem',
        }}/>
      </a>
    </Box>
  )
}

const NavItem = ({ text, onPressed, currentLocation, icon, isActive, disabled, item, filter }) => {
  if(item.subPaths){
    return (
      <SubMenuItem
        filter={filter}
        text={text}
        icon={icon}
        onPressed={onPressed}
        currentLocation={currentLocation}
        isActive={isActive}
        disabled={disabled}
        item={item} />
    )
  }

  return (
    <ListItem disablePadding>
      <Button
        startIcon={icon}
        fullWidth
        sx={{
        paddingTop: '1rem',
        paddingBottom: '1rem',
        justifyContent: 'flex-start',
        color: 'emphasis.contrastText',
        backgroundColor: isActive ? 'primary.dark' : 'transparent',
        textTransform: 'none',
        fontSize: '1rem',
        ":hover": {
          backgroundColor: 'primary.main',
          color: 'emphasis.contrastText',
        },
        // Ensure that the disabled button isn't greyed out
        "&.Mui-disabled": {
          background: 'primary.main',
          color: 'emphasis.contrastText',
        },
      }} disabled={disabled} onClick={onPressed}>
        {text}
      </Button>
    </ListItem>
  )
}

const SubMenuItem = ({ text, onPressed, currentLocation, icon, item, filter }) => {
  return (
    <ListItem disablePadding>
      <Box sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
      }}>
        <NavItem
          text={text}
          onPressed={() => {}} // no-op
          isActive={false}
          icon={icon}
          disabled={true}
          item={{}} />
        <List sx={{ paddingLeft: '1rem' }}>
          {item.subPaths
          .filter(filter)
          .map((val, index) => (
            <NavItem
              key={index}
              text={val.name}
              icon={val.icon}
              onPressed={() => {onPressed({ path: val.path })}}
              currentLocation={currentLocation}
              isActive={currentLocation.includes(val.path)}
              disabled={false}
              item={val}
            />
          ))}
        </List>
      </Box>
    </ListItem>
  )
}

const LogOut = ({ isActive, signOut }) => {
  return (
    <Box padding='1.5rem'>
      <Button
        startIcon={<Logout />}
        fullWidth
        sx={{
        paddingTop: '1rem',
        paddingBottom: '1rem',
        marginTop: '0.5rem',
        marginBottom: '0.5rem',
        justifyContent: 'flex-start',
        color: 'emphasis.contrastText',
        backgroundColor: isActive ? 'primary.dark' : 'transparent',
        textTransform: 'none',
        fontSize: '1rem',
        ":hover": {
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
        },
      }} onClick={() => signOut()}>
        Log Out
      </Button>
    </Box>
  )
}

export default Drawer
